import React, { useMemo, useState } from 'react';
import { ShieldCheckIcon, AlertTriangleIcon, XCircleIcon, CheckCircleIcon, ActivityIcon, ZapIcon, TrendingUpIcon, ClockIcon, PlayIcon, RefreshCwIcon, FileTextIcon, BarChart2Icon } from 'lucide-react';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { ALL_QUESTIONS_CORE, getSeedStats, QUESTIONS_BY_FAMILY } from '../../../data/questionLoader';
import { runPipelineBatch } from '../../../governance/pipeline/orchestrator';
import type { PipelineBatchResult } from '../../../governance/pipeline/issueContract';
import type { Question } from '../../../types';
// ─── Sub-components ───────────────────────────────────────────────────────────
interface StatCardProps {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  trend?: string;
  trendUp?: boolean;
  isLoading?: boolean;
}
function StatCard({
  label,
  value,
  icon,
  color,
  bgColor,
  trend,
  trendUp,
  isLoading
}: StatCardProps) {
  return <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm" data-id="element-3316">
      <div className="flex items-start justify-between" data-id="element-3317">
        <div data-id="element-3318">
          <p className="text-sm font-medium text-gray-500" data-id="element-3319">{label}</p>
          {isLoading ? <div className="mt-2 h-8 w-16 animate-pulse rounded bg-gray-100" data-id="element-3320" /> : <p className={`mt-1 text-3xl font-bold ${color}`} data-id="element-3321">{value}</p>}
          {trend && !isLoading && <p className={`mt-1 text-xs font-medium ${trendUp ? 'text-emerald-600' : 'text-red-500'}`} data-id="element-3322">
              {trendUp ? '↑' : '↓'} {trend}
            </p>}
        </div>
        <div className={`rounded-lg p-2.5 ${bgColor}`} data-id="element-3323">{icon}</div>
      </div>
    </div>;
}
interface PipelineStageRowProps {
  name: string;
  label: string;
  status: 'ok' | 'warning' | 'error' | 'pending';
  count: number;
  description: string;
}
function PipelineStageRow({
  name,
  label,
  status,
  count,
  description
}: PipelineStageRowProps) {
  const statusConfig = {
    ok: {
      dot: 'bg-emerald-500',
      text: 'text-emerald-700',
      bg: 'bg-emerald-50',
      label: 'OK'
    },
    warning: {
      dot: 'bg-amber-500',
      text: 'text-amber-700',
      bg: 'bg-amber-50',
      label: 'Waarschuwing'
    },
    error: {
      dot: 'bg-red-500',
      text: 'text-red-700',
      bg: 'bg-red-50',
      label: 'Fout'
    },
    pending: {
      dot: 'bg-gray-300',
      text: 'text-gray-500',
      bg: 'bg-gray-50',
      label: 'Niet uitgevoerd'
    }
  };
  const cfg = statusConfig[status];
  return <div className="flex items-center gap-4 rounded-lg border border-gray-100 bg-white px-4 py-3" data-id="element-3324">
      <div className={`h-3 w-3 rounded-full ${cfg.dot} flex-shrink-0`} data-id="element-3325" />
      <div className="min-w-0 flex-1" data-id="element-3326">
        <div className="flex items-center gap-2" data-id="element-3327">
          <span className="text-sm font-semibold text-gray-800" data-id="element-3328">{label}</span>
          <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500" data-id="element-3329">
            {name}
          </code>
        </div>
        <p className="text-xs text-gray-500" data-id="element-3330">{description}</p>
      </div>
      <div className={`rounded-full px-2.5 py-1 text-xs font-semibold ${cfg.bg} ${cfg.text}`} data-id="element-3331">
        {status === 'pending' ? cfg.label : count > 0 ? `${count} issues` : cfg.label}
      </div>
    </div>;
}
interface ActivityItemProps {
  time: string;
  message: string;
  type: 'success' | 'warning' | 'error' | 'info';
}
function ActivityItem({
  time,
  message,
  type
}: ActivityItemProps) {
  const icons = {
    success: <CheckCircleIcon className="h-4 w-4 text-emerald-500" data-id="element-3332" />,
    warning: <AlertTriangleIcon className="h-4 w-4 text-amber-500" data-id="element-3333" />,
    error: <XCircleIcon className="h-4 w-4 text-red-500" data-id="element-3334" />,
    info: <ActivityIcon className="h-4 w-4 text-blue-500" data-id="element-3335" />
  };
  return <div className="flex items-start gap-3 py-2.5" data-id="element-3336">
      <div className="mt-0.5 flex-shrink-0" data-id="element-3337">{icons[type]}</div>
      <div className="min-w-0 flex-1" data-id="element-3338">
        <p className="text-sm text-gray-700" data-id="element-3339">{message}</p>
        <p className="text-xs text-gray-400" data-id="element-3340">{time}</p>
      </div>
    </div>;
}
// ─── Stage domain mapping ─────────────────────────────────────────────────────
const PIPELINE_STAGE_DEFS = [{
  name: 'schemaStage',
  label: 'Schema Validatie',
  domain: 'schema' as const,
  description: 'JSON-structuur en verplichte velden worden gecontroleerd'
}, {
  name: 'typeStage',
  label: 'Vraagtype Validatie',
  domain: 'type' as const,
  description: 'Vraagtype-specifieke regels en antwoordformaten'
}, {
  name: 'contentStage',
  label: 'Inhoud & Taal',
  domain: 'content' as const,
  description: 'Taalgebruik, moeilijkheidsgraad en didactische kwaliteit'
}, {
  name: 'supportStage',
  label: 'Hints & Uitleg',
  domain: 'support' as const,
  description: 'Aanwezigheid en kwaliteit van ondersteunend materiaal'
}, {
  name: 'visualStage',
  label: 'Visuele Hulpmiddelen',
  domain: 'visual' as const,
  description: 'Visuele componenten en antwoordlekvalidatie'
}, {
  name: 'graphStage',
  label: 'LDG-Graaf',
  domain: 'graph' as const,
  description: 'Leerpad-afhankelijkheden en DAG-integriteit'
}];
function domainToStatus(count: number): 'ok' | 'warning' | 'error' {
  if (count === 0) return 'ok';
  if (count <= 5) return 'warning';
  return 'error';
}
// ─── Main component ───────────────────────────────────────────────────────────
export function GovernanceOverviewTab() {
  const [isRunning, setIsRunning] = useState(false);
  const [batchResult, setBatchResult] = useState<PipelineBatchResult | null>(null);
  const [lastRun, setLastRun] = useState<string | null>(null);
  const [runLog, setRunLog] = useState<ActivityItemProps[]>([]);
  const [sampleSize, setSampleSize] = useState(100);
  // ── Instant stats (no pipeline needed) ──────────────────────────────────────
  const seedStats = useMemo(() => getSeedStats(), []);
  const totalFamilies = useMemo(() => QUESTIONS_BY_FAMILY.size, []);
  const totalQuestions = ALL_QUESTIONS_CORE.length;
  // ── Derived pipeline stats ───────────────────────────────────────────────────
  const blockedCount = batchResult?.verdicts.FAIL ?? null;
  const reviewCount = batchResult?.verdicts.REVIEW_NEEDED ?? null;
  const avgScore = batchResult?.avgScore ?? null;
  const stageIssueCounts = useMemo(() => {
    if (!batchResult) return null;
    return batchResult.issuesByDomain;
  }, [batchResult]);
  // ── Health status ────────────────────────────────────────────────────────────
  const overallHealth = useMemo(() => {
    if (!batchResult) return 'pending' as const;
    if (batchResult.verdicts.FAIL > 0) return 'error' as const;
    if (batchResult.verdicts.REVIEW_NEEDED > 0) return 'warning' as const;
    return 'ok' as const;
  }, [batchResult]);
  const healthConfig = {
    pending: {
      label: 'Niet uitgevoerd',
      color: 'text-gray-600',
      bg: 'bg-gray-50',
      border: 'border-gray-200',
      dot: 'bg-gray-400'
    },
    ok: {
      label: 'Gezond',
      color: 'text-emerald-700',
      bg: 'bg-emerald-50',
      border: 'border-emerald-200',
      dot: 'bg-emerald-500'
    },
    warning: {
      label: 'Aandacht vereist',
      color: 'text-amber-700',
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      dot: 'bg-amber-500'
    },
    error: {
      label: 'Kritieke fouten',
      color: 'text-red-700',
      bg: 'bg-red-50',
      border: 'border-red-200',
      dot: 'bg-red-500'
    }
  };
  const hcfg = healthConfig[overallHealth];
  // ── Run validation ───────────────────────────────────────────────────────────
  const handleValidate = () => {
    setIsRunning(true);
    const startTime = Date.now();
    const now = new Date();
    const timeStr = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    // Run in next tick to allow UI to update
    setTimeout(() => {
      try {
        const sample = ALL_QUESTIONS_CORE.slice(0, sampleSize) as unknown as Question[];
        const result = runPipelineBatch(sample);
        const duration = ((Date.now() - startTime) / 1000).toFixed(1);
        setBatchResult(result);
        setLastRun(`${timeStr} (${duration}s)`);
        // Build activity log from results
        const newLog: ActivityItemProps[] = [];
        if (result.verdicts.FAIL > 0) {
          newLog.push({
            time: timeStr,
            message: `${result.verdicts.FAIL} geblokkeerde vragen gevonden in steekproef van ${sampleSize}`,
            type: 'error'
          });
        }
        if (result.verdicts.REVIEW_NEEDED > 0) {
          newLog.push({
            time: timeStr,
            message: `${result.verdicts.REVIEW_NEEDED} vragen vereisen review (hoge coach-ernst)`,
            type: 'warning'
          });
        }
        if (result.verdicts.PASS > 0) {
          newLog.push({
            time: timeStr,
            message: `${result.verdicts.PASS} vragen geslaagd — gem. score ${result.avgScore}%`,
            type: 'success'
          });
        }
        newLog.push({
          time: timeStr,
          message: `Validatie voltooid: ${sampleSize} vragen in ${duration}s`,
          type: 'info'
        });
        setRunLog(prev => [...newLog, ...prev].slice(0, 8));
      } catch (err) {
        setRunLog(prev => [{
          time: timeStr,
          message: 'Validatie mislukt — zie console voor details',
          type: 'error' as const
        }, ...prev].slice(0, 8));
      } finally {
        setIsRunning(false);
      }
    }, 50);
  };
  return <div className="space-y-6" data-id="element-3341">
      {/* Pipeline Health Banner */}
      <div className={`flex items-center justify-between rounded-xl border ${hcfg.border} ${hcfg.bg} px-5 py-4`} data-id="element-3342">
        <div className="flex items-center gap-3" data-id="element-3343">
          <div className={`h-3 w-3 rounded-full ${hcfg.dot} ${overallHealth !== 'pending' ? 'animate-pulse' : ''}`} data-id="element-3344" />
          <div data-id="element-3345">
            <p className={`font-semibold ${hcfg.color}`} data-id="element-3346">
              Pipeline Status: {hcfg.label}
            </p>
            <p className="text-sm text-gray-500" data-id="element-3347">
              {lastRun ? `Laatste validatie: ${lastRun} — steekproef ${sampleSize} vragen` : 'Klik op "Valideer Nu" om de pipeline uit te voeren'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3" data-id="element-3348">
          <select value={sampleSize} onChange={e => setSampleSize(Number(e.target.value))} disabled={isRunning} className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs focus:outline-none" data-id="element-3349">
            <option value={50} data-id="element-3350">50 vragen</option>
            <option value={100} data-id="element-3351">100 vragen</option>
            <option value={250} data-id="element-3352">250 vragen</option>
            <option value={totalQuestions} data-id="element-3353">Alle ({totalQuestions})</option>
          </select>
          <Button variant="primary" size="sm" onClick={handleValidate} disabled={isRunning} data-id="element-3354">
            {isRunning ? <span className="flex items-center gap-2" data-id="element-3355">
                <RefreshCwIcon className="h-4 w-4 animate-spin" data-id="element-3356" />
                Valideren...
              </span> : <span className="flex items-center gap-2" data-id="element-3357">
                <PlayIcon className="h-4 w-4" data-id="element-3358" />
                Valideer Nu
              </span>}
          </Button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-id="element-3359">
        <StatCard label="Totaal Vragen" value={totalQuestions.toLocaleString('nl-NL')} icon={<FileTextIcon className="h-5 w-5 text-blue-600" data-id="element-3361" />} color="text-blue-700" bgColor="bg-blue-50" trend={`${totalFamilies} families`} trendUp={true} data-id="element-3360" />
        <StatCard label="Modules" value={Object.keys(seedStats.byModule).length} icon={<ShieldCheckIcon className="h-5 w-5 text-violet-600" data-id="element-3363" />} color="text-violet-700" bgColor="bg-violet-50" trend={`${totalFamilies} families`} trendUp={true} data-id="element-3362" />
        <StatCard label="Geblokkeerde Vragen" value={blockedCount !== null ? blockedCount : '—'} icon={<XCircleIcon className="h-5 w-5 text-red-500" data-id="element-3365" />} color={blockedCount === 0 ? 'text-emerald-600' : 'text-red-600'} bgColor={blockedCount === 0 ? 'bg-emerald-50' : 'bg-red-50'} trend={blockedCount !== null ? `in steekproef van ${sampleSize}` : 'Valideer om te zien'} trendUp={blockedCount === 0} isLoading={isRunning} data-id="element-3364" />
        <StatCard label="Kwaliteitsscore" value={avgScore !== null ? `${avgScore}%` : '—'} icon={<TrendingUpIcon className="h-5 w-5 text-emerald-600" data-id="element-3367" />} color={avgScore === null ? 'text-gray-400' : avgScore >= 80 ? 'text-emerald-700' : avgScore >= 60 ? 'text-amber-700' : 'text-red-700'} bgColor="bg-emerald-50" trend={avgScore !== null ? `gem. over ${sampleSize} vragen` : 'Valideer om te zien'} trendUp={avgScore !== null && avgScore >= 80} isLoading={isRunning} data-id="element-3366" />
      </div>

      {/* Pipeline Stages + Activity */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2" data-id="element-3368">
        {/* Pipeline Stages */}
        <div data-id="element-3369">
          <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500" data-id="element-3370">
            <ZapIcon className="h-4 w-4" data-id="element-3371" />
            Pipeline Fasen
            {batchResult && <span className="ml-auto text-xs font-normal normal-case text-gray-400" data-id="element-3372">
                steekproef {sampleSize} vragen
              </span>}
          </h3>
          <div className="space-y-2" data-id="element-3373">
            {PIPELINE_STAGE_DEFS.map(stage => {
            const count = stageIssueCounts ? stageIssueCounts[stage.domain] ?? 0 : 0;
            const status = stageIssueCounts ? domainToStatus(count) : 'pending';
            return <PipelineStageRow key={stage.name} name={stage.name} label={stage.label} status={status} count={count} description={stage.description} data-id="element-3374" />;
          })}
          </div>
        </div>

        {/* Activity Log */}
        <div data-id="element-3375">
          <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500" data-id="element-3376">
            <ClockIcon className="h-4 w-4" data-id="element-3377" />
            Activiteitenlog
          </h3>
          <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm min-h-[200px]" data-id="element-3378">
            {runLog.length === 0 ? <div className="flex h-full items-center justify-center py-8" data-id="element-3379">
                <p className="text-sm text-gray-400" data-id="element-3380">
                  Nog geen validaties uitgevoerd. Klik op "Valideer Nu".
                </p>
              </div> : <div className="divide-y divide-gray-50" data-id="element-3381">
                {runLog.map((item, i) => <ActivityItem key={i} {...item} data-id="element-3382" />)}
              </div>}
          </div>
        </div>
      </div>

      {/* Module Breakdown */}
      {Object.keys(seedStats.byModule).length > 0 && <div data-id="element-3383">
          <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500" data-id="element-3384">
            <BarChart2Icon className="h-4 w-4" data-id="element-3385" />
            Modules Overzicht
          </h3>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-id="element-3386">
            {Object.entries(seedStats.byModule).map(([moduleId, stats]) => <div key={moduleId} className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm" data-id="element-3387">
                <div className="flex items-center justify-between mb-2" data-id="element-3388">
                  <code className="text-xs font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded" data-id="element-3389">
                    {moduleId}
                  </code>
                  <span className="text-xs text-gray-400" data-id="element-3390">
                    {stats.families} families
                  </span>
                </div>
                <p className="text-2xl font-bold text-gray-800" data-id="element-3391">
                  {stats.count}
                </p>
                <p className="text-xs text-gray-500" data-id="element-3392">vragen</p>
                <p className="text-xs text-gray-400 mt-1" data-id="element-3393">
                  Tier {stats.tierRange[0]}–{stats.tierRange[1]}
                </p>
              </div>)}
          </div>
        </div>}
    </div>;
}